import {useEffect, useState} from 'react';
import {Box, FormControl, FormHelperText, Grid, MenuItem, TextField, Tooltip, Typography} from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import {fetchCustomer, fetchCustomers, fetchJobs} from "../../../utils/apiCalls";
import {fetchJobList} from "../../../utils/dashboardCalls";
import {goldErrorFieldSx, useStyles} from "../../../utils/uiUtils";
import {getBillingDescriptionValidationRules, projectDeliverablesValidationRules, projectScopeValidationRules, quoteReferenceIdValidationRules} from "../../../utils/validationRules";
import {Link} from "react-router";

export const Job = (props) => {

    const classes = useStyles();
    const [customerList, setCustomerList] = useState([]);
    const [jobList, setJobList] = useState([]);
    const [usedJobIds, setUsedJobIds] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (props.customers) {
            setCustomerList(props.customers);
            return;
        }
        fetchCustomers()
            .then(customers => setCustomerList(customers))
            .catch(error => console.log(error));
    }, [props.customers]);

    useEffect(() => {
        // Set default values if not already set
        if (!props.watch('security_privace')) {
            props.setValue('security_privace', 'confidential');
        }
        if (!props.watch('billing')) {
            props.setValue('billing', 'monthly');
        }
        if (!props.watch('sow_commence')) {
            props.setValue('sow_commence', moment());
        }
    }, []);

    useEffect(() => {
        if (props.tenant_id) {
            loadJobsForCustomer(props.tenant_id);
        }
    }, [props.tenant_id]);

    const loadJobsForCustomer = (customerUUID, resetJobId = false) => {
        setJobsLoading(true);
        setJobList([]);
        setUsedJobIds([]);
        if (resetJobId) {
            props.setValue('tapeark_job_id', '');
        }

        fetchCustomer(customerUUID)
            .then((customer) => {
                const arkbridgeId = customer.arkbridge_customer_id;
                const companyName = customer.company_name;
                return Promise.all([
                    fetchJobList(arkbridgeId),
                    fetchJobs({customerNames: [companyName]}),
                ]);
            })
            .then(([jobData, ordersData]) => {
                // Flatten job list from nested result structure
                const jobs = Object.values(jobData?.result || {})
                    .flatMap(company => company.data || []);
                setJobList(jobs);

                // Extract job IDs already used by existing orders, excluding the current order
                const currentOrderUUID = window?.location?.href?.split("uuid=")[1];
                const used = (ordersData?.results || [])
                    .filter(order => order.uuid !== currentOrderUUID && order.tapeark_job_id)
                    .map(order => order.tapeark_job_id);
                setUsedJobIds(used);
            })
            .catch(error => console.log(error))
            .finally(() => setJobsLoading(false));
    };

    const onchangesecurityPrivace = (e, newValue) => {
        props.setValue('security_privace', newValue);
    };
    const onchangeBilling = (e, newValue) => {
        props.setValue('billing', newValue);
        if (newValue !== 'other') props.setValue('billing_description', null);
    };
    const handleCompanyChange = (e) => {
        props.setValue('tenant_id', e.target.value);
        loadJobsForCustomer(e.target.value, true);
    };
    return (
        <Grid
            container
            style={{
                border: "1px solid #C7CCD0",
                borderRadius: "8px",
                marginTop: "20px",
                minHeight: "100vh",
                height: "max-content",
                marginBottom: '100px',
                alignItems: "start",
                display: "block",
            }}
        >
            <Box sx={{pt: 4, pr: 4, pb: 4, pl: 2, mb: 4}}>
                    <Grid container direction='row' justifyContent='flex-start'>
                        <Grid item xs={6}>
                            <Grid container direction='column' spacing={1}>
                                <Grid item>
                                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline'}}>
                                        <Typography className={classes.hintText}>
                                            Customer not listed? <Link to="/AccountCreation" style={{color: '#FFC540', textDecoration: 'none'}}>Create a new account</Link> first.
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={8}>
                                    {customerList?.length ? (
                                        <FormControl fullWidth={true}>
                                            <TextField
                                                className={classes.selectLabelGreyDiff}
                                                sx={!props.watch('tenant_id') && props.errors?.tenant_id ? goldErrorFieldSx : {}}
                                                label="Nexus Registered Customer *"
                                                InputLabelProps={{shrink: true}}
                                                fullWidth={true}
                                                name='tenant_id'
                                                select
                                                value={props.watch('tenant_id') || ''}
                                                onChange={handleCompanyChange}
                                                error={false}
                                            >
                                                {
                                                    customerList?.map((item, index) => {
                                                        return (
                                                            <MenuItem key={index} value={item.uuid}>{item.company_name}</MenuItem>
                                                        )
                                                    })
                                                }
                                            </TextField>
                                        </FormControl>) : ""}
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        select
                                        className={classes.selectLabelGreyDiff}
                                        variant="outlined"
                                        fullWidth={true}
                                        label="Tape ARK Job ID"
                                        InputLabelProps={{shrink: true}}
                                        value={jobList.some(j => j.job_id === props.watch('tapeark_job_id')) ? props.watch('tapeark_job_id') : ''}
                                        onChange={(e) => props.setValue('tapeark_job_id', e.target.value)}
                                        disabled={jobsLoading || jobList.length === 0}
                                        error={false}
                                    >
                                        {jobList.map((job) => (
                                            <MenuItem
                                                key={job.job_id}
                                                value={job.job_id}
                                                disabled={usedJobIds.includes(job.job_id)}
                                                sx={usedJobIds.includes(job.job_id) ? {opacity: 0.4} : {}}
                                            >
                                                {job.job_id}{job.description ? ` — ${job.description.length > 35 ? job.description.slice(0, 35) + '…' : job.description}` : ''}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        {...props.register('quote_reference_id', quoteReferenceIdValidationRules)}
                                        className={classes.selectLabelGreyDiff}
                                        sx={props.errors?.quote_reference_id ? goldErrorFieldSx : {}}
                                        label="Quote Reference ID *"
                                        InputLabelProps={{shrink: true}}
                                        variant="outlined"
                                        fullWidth={true}
                                        error={false}
                                    />
                                    {props.errors?.quote_reference_id && (
                                        <FormHelperText sx={{mx: 0, color: '#FFC540'}}>{props.errors.quote_reference_id.message}</FormHelperText>
                                    )}
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        {...props.register('project_scope', projectScopeValidationRules)}
                                        className={classes.selectLabelGreyDiff}
                                        sx={props.errors?.project_scope ? goldErrorFieldSx : {}}
                                        label="Project Scope *"
                                        InputLabelProps={{shrink: true}}
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        fullWidth={true}
                                        style={{lineBreak: "anywhere"}}
                                        error={false}
                                    />
                                    {props.errors?.project_scope && (
                                        <FormHelperText sx={{mx: 0, color: '#FFC540'}}>{props.errors.project_scope.message}</FormHelperText>
                                    )}
                                </Grid>
                                <Grid item xs={8}>
                                    <TextField
                                        {...props.register('project_deliverables', projectDeliverablesValidationRules)}
                                        className={classes.selectLabelGreyDiff}
                                        sx={props.errors?.project_deliverables ? goldErrorFieldSx : {}}
                                        label="Project Deliverables *"
                                        InputLabelProps={{shrink: true}}
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        fullWidth={true}
                                        error={false}
                                    />
                                    {props.errors?.project_deliverables && (
                                        <FormHelperText sx={{mx: 0, color: '#FFC540'}}>{props.errors.project_deliverables.message}</FormHelperText>
                                    )}
                                </Grid>
                                <Grid item xs={12}>

                                    <Typography align='left' className={classes.fieldLabel}><Tooltip sx={{fontSize: 14}} title="Security and Privacy Evaluation and Assessment: Using the following categories, please indicate the classification of data to reside on the Company's system (one only)">Security and Privacy Evaluation and Assessment</Tooltip></Typography>

                                </Grid>
                                <Grid item xs={8}>
                                    <Grid container direction='row' justifyContent='flex-start'>
                                        <Grid item>
                                            <FormControl>
                                                <RadioGroup
                                                    row
                                                    onChange={onchangesecurityPrivace}
                                                    value={props.watch('security_privace') || 'confidential'}
                                                >
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="critical" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Critical"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="confidential" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Confidential"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="operational" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Operational"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="public" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Public"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="other" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Other"/>
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={8}>
                                    <Grid container direction='row'>
                                        <Grid item>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    slotProps={{
                                                        textField: {
                                                            required: true,
                                                            className: classes.selectLabelGreyDiff,
                                                            label: 'Project Commence',
                                                            InputLabelProps: {shrink: true},
                                                        }
                                                    }}
                                                    onChange={(newValue) => props.setValue("sow_commence", newValue)}
                                                    defaultValue={props.watch('sow_commence') ? moment(props.watch('sow_commence')) : null}
                                                    value={props.watch('sow_commence') ? moment(props.watch('sow_commence')) : null}
                                                    name="sow_commence"
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        <Grid item>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    slotProps={{
                                                        textField: {
                                                            className: classes.selectLabelGreyDiff,
                                                            label: 'Project Completion',
                                                            InputLabelProps: {shrink: true},
                                                        }
                                                    }}
                                                    minDate={props.watch('sow_commence') ? moment(props.watch('sow_commence')) : undefined}
                                                    onChange={(newValue) => props.setValue("sow_complete", newValue)}
                                                    defaultValue={props.watch('sow_complete') ? moment(props.watch('sow_complete')) : null}
                                                    value={props.watch('sow_complete') ? moment(props.watch('sow_complete')) : null}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        {
                                            /* Compared as dates, and only once a completion date has actually
                                               been entered. This used to format both to 'DD-MM-YYYY' and
                                               compare the strings, which ranks by day of the month, and it
                                               read a blank completion as today — so it accused the user of
                                               misordering a date they had not entered. Deliberately the exact
                                               negation of isStepValid() in JobWizard, which gates the save:
                                               if the two ever disagree, Save refuses with nothing on screen
                                               to say why. Change them together. */
                                            (props.watch('sow_complete')
                                                && moment(props.watch('sow_complete')).isValid()
                                                && moment(props.watch('sow_commence')).isValid()
                                                && moment(props.watch('sow_commence')).isAfter(moment(props.watch('sow_complete')), 'day')) ?
                                                (<span style={{color: 'red'}}>Project Commence date must precede Project Completion date.</span>) : ""
                                        }
                                    </Grid>
                                </Grid>
                                <Grid item xs={8}>
                                    <Typography align='left' className={classes.fieldLabel}>Billing</Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    <Grid container direction='column' spacing={1} alignItems='flex-start'>
                                        <Grid item>
                                            <FormControl>
                                                <RadioGroup row
                                                            onChange={onchangeBilling}
                                                            value={props.watch('billing') || 'monthly'}
                                                >
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="monthly" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Monthly"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="quarterly" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Quarterly"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="half_yearly" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Half Yearly"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="annually" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Annually"/>
                                                    <FormControlLabel sx={{color: '#C7CCD0'}} value="other" control={<Radio sx={{color: '#C7CCD0', '&.Mui-checked': {color: '#FFC540'}}}/>} label="Other"/>
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                        {props.watch('billing') === 'other' && (
                                            <Grid item>
                                                <TextField
                                                    {...props.register('billing_description', getBillingDescriptionValidationRules(props.watch))}
                                                    label="Billing Description *"
                                                    className={classes.selectLabelGreyDiff}
                                                    sx={props.errors?.billing_description ? goldErrorFieldSx : {}}
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    multiline
                                                    rows={2}
                                                    InputLabelProps={{shrink: true}}
                                                    error={false}
                                                />
                                                {props.errors?.billing_description && (
                                                    <FormHelperText sx={{mx: 0, color: '#FFC540'}}>
                                                        {props.errors.billing_description.message}
                                                    </FormHelperText>
                                                )}
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
            </Box>
        </Grid>
    )
}
